import React, { useEffect, useRef } from "react";

export default function BookingAlertOverlay({ booking, isVisible, onAccept, onCancel, isProcessing }) {
  const audioRef = useRef(null);
  const intervalRef = useRef(null);
  const fromValue = booking?.From || booking?.fromLocation || booking?.WorkArea || "";
  const toValue = booking?.TO || booking?.toLocation || "";
  const workScopeValue = booking?.WorkScope || booking?.workScope || "";
  const scheduledTimeValue = booking?.bsTime || booking?.bookedDate || "";

  // Play sound immediately when overlay becomes visible
  useEffect(() => {
    if (isVisible && booking) {
      console.log("BookingAlertOverlay: Playing sound immediately");
      
      // Stop any existing audio first
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      // Use Web Audio API for immediate, reliable sound
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        
        // Resume audio context if suspended (browser autoplay policy)
        if (audioContext.state === 'suspended') {
          audioContext.resume();
        }
        
        const playRingTone = () => {
          const now = audioContext.currentTime;
          
          // Uber-style attention chime - pleasant but urgent
          // First chime (ding)
          const osc1 = audioContext.createOscillator();
          const gain1 = audioContext.createGain();
          osc1.connect(gain1);
          gain1.connect(audioContext.destination);
          osc1.frequency.value = 880; // A5
          osc1.type = "sine";
          gain1.gain.setValueAtTime(0.7, now);
          gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
          osc1.start(now);
          osc1.stop(now + 0.3);

          // Second chime (higher ding)
          const osc2 = audioContext.createOscillator();
          const gain2 = audioContext.createGain();
          osc2.connect(gain2);
          gain2.connect(audioContext.destination);
          osc2.frequency.value = 1318.51; // E6
          osc2.type = "sine";
          gain2.gain.setValueAtTime(0.7, now + 0.15);
          gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
          osc2.start(now + 0.15);
          osc2.stop(now + 0.5);

          // Third sustained tone (attention grabber)
          const osc3 = audioContext.createOscillator();
          const gain3 = audioContext.createGain();
          osc3.connect(gain3);
          gain3.connect(audioContext.destination);
          osc3.frequency.value = 1760; // A6
          osc3.type = "sine";
          gain3.gain.setValueAtTime(0.6, now + 0.35);
          gain3.gain.exponentialRampToValueAtTime(0.01, now + 0.8);
          osc3.start(now + 0.35);
          osc3.stop(now + 0.8);

          // Soft low undertone for richness
          const osc4 = audioContext.createOscillator();
          const gain4 = audioContext.createGain();
          osc4.connect(gain4);
          gain4.connect(audioContext.destination);
          osc4.frequency.value = 440; // A4
          osc4.type = "triangle";
          gain4.gain.setValueAtTime(0.3, now);
          gain4.gain.exponentialRampToValueAtTime(0.01, now + 0.6);
          osc4.start(now);
          osc4.stop(now + 0.6);
        };

        // Play immediately
        playRingTone();
        
        // Repeat every 2 seconds
        intervalRef.current = setInterval(playRingTone, 2000);
        
        // Store reference for cleanup
        audioRef.current = { 
          context: audioContext,
          pause: () => {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          }
        };

        console.log("BookingAlertOverlay: Ring tone started");

      } catch (e) {
        console.error("Web Audio API failed:", e);
      }

      // Vibrate on mobile
      if (navigator.vibrate) {
        navigator.vibrate([400, 200, 400, 200, 400]);
      }
    }

    // Cleanup when overlay closes or component unmounts
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [isVisible, booking]);

  // Stop sound and accept
  const handleAccept = () => {
    console.log("BookingAlertOverlay: Accept clicked - stopping sound");
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    onAccept();
  };

  // Stop sound and cancel
  const handleCancel = () => {
    console.log("BookingAlertOverlay: Cancel clicked - stopping sound");
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    onCancel();
  };

  if (!isVisible || !booking) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center px-3 sm:px-4" style={{ zIndex: 9999 }}>
      <div
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        style={{ zIndex: 9999 }}
      />
      <div className="relative bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-md p-6 sm:p-8 text-center animate-pulse" style={{ zIndex: 10000 }}>
        <div className="text-4xl sm:text-5xl mb-3 sm:mb-4">🚗</div>
        <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 mb-4 sm:mb-6">New Trip Request!</h2>
        <div className="space-y-2 sm:space-y-3 text-base sm:text-lg">
          <p><strong>Booking ID:</strong> #{booking.bId}</p>
          <p><strong>Employee:</strong> {booking.employeeName || "Employee"}</p>
          <p><strong>Distance:</strong> {booking.totalDestence ? `${booking.totalDestence} km` : "Calculating..."}</p>
          {scheduledTimeValue && (
            <p><strong>Scheduled:</strong> {scheduledTimeValue}</p>
          )}
          {(fromValue || toValue) && (
            <p><strong>Route:</strong> {fromValue || "N/A"} {toValue ? `-> ${toValue}` : ""}</p>
          )}
          {workScopeValue && (
            <p><strong>Work Scope:</strong> {workScopeValue}</p>
          )}
        </div>
        <div className="flex gap-3 sm:gap-4 mt-6 sm:mt-8">
          <button
            onClick={handleAccept}
            disabled={isProcessing}
            className={`flex-1 font-bold text-base sm:text-lg py-3 sm:py-4 rounded-lg sm:rounded-xl shadow-lg transition transform ${
              isProcessing 
                ? "bg-gray-400 cursor-not-allowed" 
                : "bg-green-600 hover:bg-green-700 hover:scale-105"
            } text-white`}
          >
            {isProcessing ? "Processing..." : "✓ Accept"}
          </button>
          <button
            onClick={handleCancel}
            disabled={isProcessing}
            className={`flex-1 font-bold text-base sm:text-lg py-3 sm:py-4 rounded-lg sm:rounded-xl shadow-lg transition transform ${
              isProcessing 
                ? "bg-gray-400 cursor-not-allowed" 
                : "bg-red-600 hover:bg-red-700 hover:scale-105"
            } text-white`}
          >
            {isProcessing ? "Processing..." : "✗ Decline"}
          </button>
        </div>
      </div>
    </div>
  );
}
